import { URL_LOCALE } from "@/config/localePath";
import type { IRootState } from "@/redux/store";
import { safeGetItem, safeSetItem } from "@/utils/safeStorage";
import { useAdFree } from "@repo/shared/redux";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

import {
  WINGS_CUP_CONFIG,
  WINGS_CUP_ENABLED,
  WINGS_CUP_PREVIEW,
  type WingsCupCopy,
  type WingsCupCountry,
  type WingsCupLang,
} from "./wingsCupConfig";

export type WingsCup = {
  country: WingsCupCountry;
  lang: WingsCupLang;
  url: string;
  copy: WingsCupCopy;
};

/*
  QA override: ?rbwc=DK (or SE, NO) forces the campaign on for that country
  and ignores the date window. Kept for the tab's session, since in-app links
  drop the query string; ?rbwc=off clears it. The kill switch still wins.
  Read once at load too (bottom of this file): some pages rewrite their query
  string before any unit renders.
*/
const OVERRIDE_PARAM = "rbwc";
const OVERRIDE_STORAGE_KEY = "rbwc-override";

const MARKETS = Object.keys(WINGS_CUP_CONFIG.markets);
const isMarket = (code: string | undefined): code is WingsCupCountry =>
  !!code && MARKETS.includes(code);

const readOverride = (search: string): WingsCupCountry | undefined => {
  const param = new URLSearchParams(search).get(OVERRIDE_PARAM)?.toUpperCase();
  try {
    if (isMarket(param)) sessionStorage.setItem(OVERRIDE_STORAGE_KEY, param);
    else if (param) sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
    const stored = sessionStorage.getItem(OVERRIDE_STORAGE_KEY) ?? undefined;
    return isMarket(stored) ? stored : undefined;
  } catch {
    return isMarket(param) ? param : undefined;
  }
};

const isLive = (now: number) =>
  now >= Date.parse(WINGS_CUP_CONFIG.start) &&
  now < Date.parse(WINGS_CUP_CONFIG.end);

/**
 * The campaign for a visitor, or null when nothing should render: switched
 * off, a subscriber, outside the window, another market, or a country still
 * loading. The
 * country decides who sees it and which sign-up page it links to; the copy
 * follows the site language: Danish on the DA site, English everywhere else.
 * Staging ignores the date window (see WINGS_CUP_PREVIEW).
 */
export const resolveWingsCup = ({
  countryCode,
  siteLocale,
  subscriber,
  search,
  now,
  enabled = WINGS_CUP_ENABLED,
  preview = WINGS_CUP_PREVIEW,
}: {
  countryCode: string | undefined;
  /** The site's UI locale, e.g. "da-DK"; null on the unprefixed English site. */
  siteLocale: string | null;
  /** Subscribers are ad-free, so they never see it; the QA override still shows. */
  subscriber: boolean;
  search: string;
  now: number;
  enabled?: boolean;
  preview?: boolean;
}): WingsCup | null => {
  if (!enabled) return null;

  const override = readOverride(search);
  if (subscriber && !override) return null;
  const country = override ?? countryCode?.toUpperCase();
  if (!isMarket(country)) return null;
  if (!override && !preview && !isLive(now)) return null;

  const lang: WingsCupLang = siteLocale?.startsWith("da") ? "da" : "en";
  const { url } = WINGS_CUP_CONFIG.markets[country];
  return { country, lang, url, copy: WINGS_CUP_CONFIG.copy[lang] };
};

// Filled from /payments/prices on load; "" until it answers.
const countryCodeSelector = (state: IRootState) =>
  state.userReducer.countryCode;

const COUNTRY_CACHE_KEY = "lastCountryCode";

/**
 * The visitor's country, or "" while unknown. Falls back to the last visit's,
 * so the menu does not show one card and swap to another when prices answer.
 */
export const useVisitorCountry = (): string => {
  const countryCode = useSelector(countryCodeSelector);
  useEffect(() => {
    if (countryCode) safeSetItem(COUNTRY_CACHE_KEY, countryCode);
  }, [countryCode]);
  return countryCode || safeGetItem(COUNTRY_CACHE_KEY) || "";
};

/** resolveWingsCup for the current visitor and URL. */
export const useWingsCup = (): WingsCup | null => {
  const { search } = useLocation();
  const countryCode = useVisitorCountry();
  const subscriber = useAdFree();
  return resolveWingsCup({
    countryCode,
    siteLocale: URL_LOCALE,
    subscriber,
    search,
    now: Date.now(),
  });
};

if (typeof window !== "undefined") readOverride(window.location.search);
